import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, Plus, Trash2, Save, Send, FileText, User, Calendar,
  Search, Building2, UserCircle, X, Check, CreditCard, Banknote, MapPin, Settings, Edit2,
  Coins, Gift, AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Catégories de produits pour la fidélité
const PRODUCT_CATEGORIES = [
  { value: 'reparation', label: 'Réparation', color: 'bg-blue-100 text-blue-700' },
  { value: 'accessoire', label: 'Accessoire', color: 'bg-green-100 text-green-700' },
  { value: 'occasion', label: 'Téléphone occasion', color: 'bg-orange-100 text-orange-700' },
  { value: 'neuf', label: 'Téléphone neuf', color: 'bg-purple-100 text-purple-700' },
  { value: 'dematerialise', label: 'Dématérialisé (exclu)', color: 'bg-gray-100 text-gray-500' },
  { value: 'autre', label: 'Autre', color: 'bg-slate-100 text-slate-700' }
];

export default function InvoiceFormPage({ user, editMode = false }) {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const invoiceType = searchParams.get('type') || 'invoice';
  
  const [loading, setLoading] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(editMode);
  const [savingDraft, setSavingDraft] = useState(false);
  const [clients, setClients] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(false); // Mode édition vs création
  const [priceMode, setPriceMode] = useState('ht'); // 'ht' or 'ttc'
  
  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [showClientResults, setShowClientResults] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const searchRef = useRef(null);
  
  // Fidélité
  const [clientPoints, setClientPoints] = useState(null);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('autre');
  const [usePoints, setUsePoints] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [discountOptions, setDiscountOptions] = useState([]);
  const [showPointsOnInvoice, setShowPointsOnInvoice] = useState(false); // Option affichage points sur facture
  
  // Address autocomplete
  const [addressSearch, setAddressSearch] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [showAddressResults, setShowAddressResults] = useState(false);
  const addressRef = useRef(null);

  const [formData, setFormData] = useState({
    client_id: '',
    issue_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: '',
    payment_terms: 'a_reception',
    payment_method: '', // CB, Espèces, Virement, Mixte
    notes: '',
    terms: 'Paiement à réception de facture',
    title: '',
    is_paid: false, // Option pour marquer comme payé
    items: [
      { description: '', quantity: 1, unit_price: 0, tva_rate: 20, discount: 0 }
    ]
  });

  const [newClient, setNewClient] = useState({
    client_type: 'professionnel',
    name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    siret: '',
    tva_number: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchClients();
    fetchCompanyInfo();
    fetchCompanySettings();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowClientResults(false);
      }
      if (addressRef.current && !addressRef.current.contains(e.target)) {
        setShowAddressResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  // Charger les paramètres fidélité
  const fetchLoyaltySettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/loyalty/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoyaltySettings(response.data);
    } catch (error) {
      console.error('Error fetching loyalty settings:', error);
    }
  };

  // Charger les points du client sélectionné
  const fetchClientPoints = async (clientId) => {
    if (!clientId) {
      setClientPoints(null);
      setDiscountOptions([]);
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}/api/loyalty/points/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientPoints(response.data);
      // Réinitialiser l'utilisation des points
      setUsePoints(false);
      setPointsToUse(0);
      setLoyaltyDiscount(0);
    } catch (error) {
      console.error('Error fetching client points:', error);
      setClientPoints(null);
    }
  };

  // Calculer les options de remise disponibles
  const calculateDiscountOptions = async (totalTTC, category) => {
    if (!selectedClient?.id || !totalTTC || totalTTC <= 0) {
      setDiscountOptions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/loyalty/clients/${selectedClient.id}/calculate-discount?sale_amount=${totalTTC}&category=${category}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiscountOptions(response.data.options || []);
    } catch (error) {
      console.error('Error calculating discount:', error);
      setDiscountOptions([]);
    }
  };

  // Appliquer une option de remise
  const applyDiscountOption = (option) => {
    if (option) {
      setUsePoints(true);
      setPointsToUse(option.points_needed);
      setLoyaltyDiscount(option.discount_value);
    } else {
      setUsePoints(false);
      setPointsToUse(0);
      setLoyaltyDiscount(0);
    }
  };

  // Charger les settings fidélité au démarrage
  useEffect(() => {
    fetchLoyaltySettings();
  }, []);

  // Charger les points quand le client change
  useEffect(() => {
    if (selectedClient?.id) {
      fetchClientPoints(selectedClient.id);
    } else {
      setClientPoints(null);
    }
  }, [selectedClient?.id]);

  // Recalculer les options de remise quand le total ou la catégorie change
  useEffect(() => {
    const totals = calculateTotals();
    if (selectedClient?.id && totals.total > 0 && selectedCategory !== 'dematerialise') {
      calculateDiscountOptions(totals.total, selectedCategory);
    } else {
      setDiscountOptions([]);
    }
  }, [formData.items, selectedCategory, selectedClient?.id]);

  // Address autocomplete using French government API
  const searchAddress = async (query) => {
    if (query.length < 3) {
      setAddressResults([]);
      return;
    }
    
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      setAddressResults(data.features || []);
      setShowAddressResults(true);
    } catch (error) {
      console.error('Address search error:', error);
    }
  };

  // Charger la facture en mode édition
  useEffect(() => {
    if (editMode && editId) {
      fetchInvoiceForEdit();
    }
  }, [editMode, editId]);

  const fetchInvoiceForEdit = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invoices/${editId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const invoice = response.data;
      
      // Pré-remplir le formulaire avec les données existantes
      setFormData({
        client_id: invoice.client_id || '',
        issue_date: invoice.issue_date || format(new Date(), 'yyyy-MM-dd'),
        due_date: invoice.due_date || '',
        payment_terms: invoice.payment_terms || 'a_reception',
        payment_method: invoice.payment_method || '',
        notes: invoice.notes || '',
        terms: invoice.terms || '',
        title: invoice.title || '',
        items: invoice.items?.length > 0 ? invoice.items : [{
          description: '',
          quantity: 1,
          unit_price: 0,
          vat_rate: 20,
          total_ht: 0
        }]
      });
      
      // Pré-sélectionner le client
      if (invoice.client_id) {
        const clientsRes = await axios.get(`${BACKEND_URL}/api/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const client = clientsRes.data.find(c => c.id === invoice.client_id);
        if (client) {
          setSelectedClient(client);
          setClientSearch(client.name);
        }
      }
      
      setLoadingInvoice(false);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Erreur lors du chargement du document');
      navigate('/invoices');
    }
  };

  const handleSelectAddress = (feature) => {
    const props = feature.properties;
    setNewClient({
      ...newClient,
      address: props.name || '',
      postal_code: props.postcode || '',
      city: props.city || ''
    });
    setAddressSearch(props.label || '');
    setShowAddressResults(false);
  };

  // Filter clients based on search
  const filteredClients = clients.filter(client => {
    const search = clientSearch.toLowerCase();
    return (
      client.name?.toLowerCase().includes(search) ||
      client.siret?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.phone?.includes(search)
    );
  }).slice(0, 10);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setFormData({ ...formData, client_id: client.id });
    setClientSearch(client.name);
    setShowClientResults(false);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setFormData({ ...formData, client_id: '' });
    setClientSearch('');
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, tva_rate: 20, discount: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'description') {
      newItems[index][field] = value;
    } else if (field === 'unit_price' && priceMode === 'ttc') {
      // Convert TTC to HT
      const ttcValue = parseFloat(value) || 0;
      const tvaRate = newItems[index].tva_rate || 20;
      newItems[index][field] = ttcValue / (1 + tvaRate / 100);
    } else {
      newItems[index][field] = parseFloat(value) || 0;
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotals = () => {
    let totalHT = 0;
    let totalTVA = 0;

    formData.items.forEach(item => {
      const itemHT = item.quantity * item.unit_price * (1 - item.discount / 100);
      const itemTVA = itemHT * item.tva_rate / 100;
      totalHT += itemHT;
      totalTVA += itemTVA;
    });

    return {
      totalHT: totalHT.toFixed(2),
      totalTVA: totalTVA.toFixed(2),
      totalTTC: (totalHT + totalTVA).toFixed(2)
    };
  };

  const handleOpenEditClient = () => {
    if (!selectedClient) return;
    // Pré-remplir le formulaire modal avec les données du client actuel
    setNewClient({
      client_type: selectedClient.client_type || 'professionnel',
      name: selectedClient.name || '',
      email: selectedClient.email || '',
      phone: selectedClient.phone || '',
      address: selectedClient.address || '',
      postal_code: selectedClient.postal_code || '',
      city: selectedClient.city || '',
      siret: selectedClient.siret || '',
      tva_number: selectedClient.tva_number || ''
    });
    setAddressSearch(
      selectedClient.address
        ? `${selectedClient.address}${selectedClient.postal_code ? ', ' + selectedClient.postal_code : ''}${selectedClient.city ? ' ' + selectedClient.city : ''}`
        : ''
    );
    setEditingClient(true);
    setShowNewClientModal(true);
  };

  const handleCloseClientModal = () => {
    setShowNewClientModal(false);
    setEditingClient(false);
    setAddressSearch('');
    setNewClient({
      client_type: 'professionnel',
      name: '',
      email: '',
      phone: '',
      address: '',
      postal_code: '',
      city: '',
      siret: '',
      tva_number: ''
    });
  };

  const handleCreateClient = async () => {
    if (!newClient.name) {
      toast.error('Le nom du client est obligatoire');
      return;
    }

    try {
      if (editingClient && selectedClient?.id) {
        // === MODE ÉDITION : PUT ===
        await axios.put(`${BACKEND_URL}/api/clients/${selectedClient.id}`, newClient, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Fiche client mise à jour');

        // Mettre à jour le client sélectionné (sans recharger la page ni fermer la facture)
        const updatedClient = { ...selectedClient, ...newClient };
        setSelectedClient(updatedClient);
        setClientSearch(updatedClient.name);
        fetchClients();
        handleCloseClientModal();
      } else {
        // === MODE CRÉATION : POST ===
        const response = await axios.post(`${BACKEND_URL}/api/clients`, newClient, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client créé avec succès');

        // L'API retourne {id, message}, on doit récupérer le client complet
        const clientId = response.data.id;

        // Créer l'objet client avec les données qu'on a
        const createdClient = {
          id: clientId,
          ...newClient
        };

        handleSelectClient(createdClient);
        fetchClients();
        handleCloseClientModal();
      }
    } catch (error) {
      toast.error(editingClient ? 'Erreur lors de la mise à jour du client' : 'Erreur lors de la création du client');
      console.error('Client save error:', error);
    }
  };

  const handleSubmit = async (asDraft = false) => {
    if (!formData.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    if (formData.items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Veuillez remplir correctement toutes les lignes');
      return;
    }

    // Mode de paiement obligatoire seulement pour les FACTURES (pas pour les devis ni les brouillons)
    if (!asDraft && invoiceType === 'invoice' && !formData.payment_method) {
      toast.error('Veuillez sélectionner un mode de paiement');
      return;
    }

    if (asDraft) {
      setSavingDraft(true);
    } else {
      setLoading(true);
    }

    try {
      const payload = {
        invoice_type: editMode ? undefined : invoiceType,
        status: formData.is_paid ? 'paid' : (asDraft ? 'draft' : 'sent'),
        client_id: formData.client_id,
        client_name: selectedClient?.name || '',
        client_email: selectedClient?.email || '',
        client_phone: selectedClient?.phone || '',
        client_address: selectedClient?.address ? 
          `${selectedClient.address}${selectedClient.postal_code ? ', ' + selectedClient.postal_code : ''}${selectedClient.city ? ' ' + selectedClient.city : ''}` : '',
        items: formData.items,
        issue_date: formData.issue_date,
        due_date: formData.due_date || null,
        payment_method: formData.payment_method || null,
        notes: formData.notes || null,
        terms: formData.terms || 'Paiement à réception de facture',
        // Fidélité
        category: selectedCategory,
        loyalty_points_used: usePoints ? pointsToUse : null,
        loyalty_discount: usePoints ? loyaltyDiscount : null,
        show_points_on_invoice: showPointsOnInvoice,
        client_points_balance: showPointsOnInvoice ? (clientPoints?.points_balance || 0) : null
      };
      
      console.log('Creating invoice with payload:', payload);

      let response;
      if (editMode && editId) {
        // Mode édition - PUT
        response = await axios.put(`${BACKEND_URL}/api/invoices/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Document modifié avec succès');
        navigate(`/invoices/${editId}`);
      } else {
        // Mode création - POST
        response = await axios.post(`${BACKEND_URL}/api/invoices`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Message avec points si applicables
        let successMsg = asDraft 
          ? 'Brouillon enregistré' 
          : `${invoiceType === 'invoice' ? 'Facture' : 'Devis'} créé(e) avec succès`;
        
        if (response.data.points_earned && response.data.points_earned > 0) {
          successMsg += ` (+${response.data.points_earned} points fidélité)`;
        }
        if (usePoints && loyaltyDiscount > 0) {
          successMsg += ` (-${loyaltyDiscount.toFixed(2)}€ remise fidélité)`;
        }
        
        toast.success(successMsg);
        
        // Télécharger automatiquement le PDF après création (pas pour brouillon)
        if (!asDraft) {
          // Rediriger vers la page de détail avec paramètre pour télécharger
          navigate(`/invoices/${response.data.id}?download=true`);
        } else {
          navigate(`/invoices/${response.data.id}`);
        }
      }
    } catch (error) {
      console.error('Invoice save error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de la sauvegarde';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
      setSavingDraft(false);
    }
  };

  const totals = calculateTotals();
  const paymentMethods = ['Espèces', 'Carte bancaire', 'Mixte', 'Virement'];

  if (loadingInvoice) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/invoices')} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="font-heading text-xl font-bold text-slate-900">
              {editMode 
                ? 'Modifier le document' 
                : (invoiceType === 'invoice' ? 'Nouvelle Facture' : 'Nouveau Devis')
              }
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/settings')}
              variant="ghost"
              className="text-slate-600 hover:text-slate-900"
            >
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
            <Button 
              onClick={() => handleSubmit(true)} 
              variant="outline" 
              disabled={savingDraft || loading}
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Enregistrer en brouillon
            </Button>
            <Button 
              onClick={() => handleSubmit(false)} 
              disabled={loading || savingDraft}
              className="bg-green-600 hover:bg-green-700"
              data-testid="create-invoice-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              {loading ? 'Création...' : `Créer ${invoiceType === 'invoice' ? 'la facture' : 'le devis'}`}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Company Info Header */}
            {companyInfo && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                  {companySettings?.logo_url ? (
                    <img 
                      src={`${BACKEND_URL}${companySettings.logo_url}`} 
                      alt="Logo" 
                      className="w-20 h-20 object-contain"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-10 h-10 text-blue-600" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{companyInfo.legal_name || companyInfo.name}</h2>
                    <p className="text-sm text-slate-500">{companyInfo.address}</p>
                    <p className="text-sm text-slate-500">{companyInfo.postal_code} {companyInfo.city}</p>
                    <p className="text-sm text-slate-500">{companyInfo.phone}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Client Selection with Search */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Client
              </h2>

              <div className="space-y-4">
                {/* Search Input */}
                <div className="relative" ref={searchRef}>
                  <Label>Rechercher un client par nom, SIREN ou SIRET</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        setShowClientResults(true);
                        if (!e.target.value) {
                          handleClearClient();
                        }
                      }}
                      onFocus={() => setShowClientResults(true)}
                      placeholder="Rechercher votre client..."
                      className="pl-10 pr-10"
                      data-testid="client-search-input"
                    />
                    {selectedClient && (
                      <button
                        onClick={handleClearClient}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Search Results Dropdown */}
                  {showClientResults && clientSearch && !selectedClient && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                      {filteredClients.length > 0 ? (
                        filteredClients.map(client => (
                          <button
                            key={client.id}
                            onClick={() => handleSelectClient(client)}
                            className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                client.client_type === 'professionnel' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                              }`}>
                                {client.client_type === 'professionnel' ? (
                                  <Building2 className="w-4 h-4" />
                                ) : (
                                  <UserCircle className="w-4 h-4" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium text-slate-900">{client.name}</div>
                                <div className="text-xs text-slate-500">
                                  {client.siret && `SIRET: ${client.siret} • `}
                                  {client.email || client.phone || 'Pas de contact'}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-slate-500">
                          Aucun client trouvé
                        </div>
                      )}
                      <button
                        onClick={() => {
                          setShowClientResults(false);
                          setShowNewClientModal(true);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-green-50 text-green-600 font-medium border-t border-slate-200 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Créer un nouveau client
                      </button>
                    </div>
                  )}
                </div>

                {/* Selected Client Display */}
                {selectedClient && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          selectedClient.client_type === 'professionnel' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                        }`}>
                          {selectedClient.client_type === 'professionnel' ? (
                            <Building2 className="w-5 h-5" />
                          ) : (
                            <UserCircle className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{selectedClient.name}</div>
                          <div className="text-sm text-slate-500">
                            {selectedClient.phone && <span className="mr-2">{selectedClient.phone}</span>}
                            {selectedClient.email && <span>{selectedClient.email}</span>}
                          </div>
                          <div className="text-sm text-slate-500">
                            {selectedClient.address && `${selectedClient.address}, `}
                            {selectedClient.postal_code} {selectedClient.city}
                          </div>
                          {selectedClient.siret && (
                            <div className="text-xs text-slate-400">SIRET: {selectedClient.siret}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleOpenEditClient}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Modifier le client"
                          data-testid="edit-client-btn"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleClearClient}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* New Client Button */}
                {!selectedClient && (
                  <Button 
                    onClick={() => setShowNewClientModal(true)} 
                    variant="outline" 
                    size="sm"
                    data-testid="new-client-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau client
                  </Button>
                )}
              </div>
            </div>

            {/* Title */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <Label>Intitulé de la {invoiceType === 'invoice' ? 'facture' : 'devis'}</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={`Intitulé de la ${invoiceType === 'invoice' ? 'facture' : 'devis'}`}
                className="mt-1"
              />
            </div>

            {/* Items */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Prestations / Produits
                </h2>
                
                {/* Price Mode Toggle */}
                <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setPriceMode('ht')}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${
                      priceMode === 'ht' ? 'bg-white shadow text-slate-900' : 'text-slate-500'
                    }`}
                  >
                    Prix HT
                  </button>
                  <button
                    onClick={() => setPriceMode('ttc')}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${
                      priceMode === 'ttc' ? 'bg-white shadow text-slate-900' : 'text-slate-500'
                    }`}
                  >
                    Prix TTC
                  </button>
                </div>
              </div>

              {/* Items */}
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                    {/* Description en pleine largeur */}
                    <div className="mb-3">
                      <Label className="text-xs text-slate-500 mb-1 block">Désignation</Label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Description détaillée du produit ou service..."
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-y min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={2}
                      />
                    </div>
                    
                    {/* Autres champs sur une ligne */}
                    <div className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-xs text-slate-500">Quantité</Label>
                        <Input
                          type="number"
                          min="1"
                          step="0.01"
                          value={item.quantity || ''}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          className="text-center"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-xs text-slate-500">Prix {priceMode.toUpperCase()} (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={priceMode === 'ht' 
                            ? (item.unit_price || '') 
                            : (item.unit_price ? Number((item.unit_price * (1 + item.tva_rate / 100)).toFixed(2)) : '')
                          }
                          onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                          className="text-center"
                        />
                      </div>
                      <div className="col-span-3 md:col-span-3">
                        <Label className="text-xs text-slate-500">TVA (%)</Label>
                        <select
                          value={item.tva_rate}
                          onChange={(e) => handleItemChange(index, 'tva_rate', e.target.value)}
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-center"
                        >
                          <option value="0">0%</option>
                          <option value="5.5">5.5%</option>
                          <option value="10">10%</option>
                          <option value="20">20%</option>
                        </select>
                      </div>
                      <div className="col-span-1 md:col-span-3 flex justify-end items-end">
                        <Button
                          onClick={() => handleRemoveItem(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          disabled={formData.items.length === 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Line total */}
                    <div className="mt-2 text-right text-sm text-slate-500">
                      Montant HT: <span className="font-medium text-slate-700">
                        {(item.quantity * item.unit_price * (1 - item.discount / 100)).toFixed(2)} €
                      </span>
                      {' '}| TTC: <span className="font-medium text-slate-700">
                        {(item.quantity * item.unit_price * (1 - item.discount / 100) * (1 + item.tva_rate / 100)).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                ))}

                <Button onClick={handleAddItem} variant="outline" size="sm" className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une ligne
                </Button>
              </div>
            </div>

            {/* Payment Method & Dates */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Paiement & Conditions
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {/* Payment Method */}
                <div className="col-span-2">
                  <Label>Mode de paiement</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {paymentMethods.map((method) => (
                      <button
                        key={method}
                        onClick={() => setFormData({ ...formData, payment_method: method })}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
                          formData.payment_method === method
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {method === 'Carte bancaire' && <CreditCard className="w-4 h-4" />}
                        {method === 'Espèces' && <Banknote className="w-4 h-4" />}
                        {method === 'Mixte' && <><CreditCard className="w-3 h-3" /><Banknote className="w-3 h-3" /></>}
                        {method === 'Virement' && <Building2 className="w-4 h-4" />}
                        {method}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Option Marquer comme PAYÉ */}
                <div className="col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition">
                    <input
                      type="checkbox"
                      checked={formData.is_paid}
                      onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                      className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                    />
                    <div>
                      <span className="font-medium text-slate-800">Marquer comme PAYÉ</span>
                      <p className="text-sm text-slate-500">La facture sera enregistrée avec le statut "Payé"</p>
                    </div>
                    {formData.is_paid && (
                      <span className="ml-auto px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full">
                        ✓ PAYÉ
                      </span>
                    )}
                  </label>
                </div>
                
                <div>
                  <Label>Date d'émission</Label>
                  <Input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Règlement</Label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2"
                  >
                    <option value="a_reception">À réception</option>
                    <option value="15_jours">15 jours</option>
                    <option value="30_jours">30 jours</option>
                    <option value="45_jours">45 jours fin de mois</option>
                    <option value="60_jours">60 jours</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notes additionnelles..."
                    className="w-full border border-slate-200 rounded-lg px-4 py-2 min-h-[80px]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Récapitulatif</h2>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total HT</span>
                  <span className="font-medium">{totals.totalHT} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">TVA</span>
                  <span className="font-medium">{totals.totalTVA} €</span>
                </div>
                
                {/* Remise fidélité */}
                {usePoints && loyaltyDiscount > 0 && (
                  <div className="flex justify-between text-sm text-amber-600">
                    <span className="flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      Remise fidélité (-{pointsToUse} pts)
                    </span>
                    <span className="font-medium">-{loyaltyDiscount.toFixed(2)} €</span>
                  </div>
                )}
                
                <div className="flex justify-between text-xl font-bold pt-3 border-t border-slate-200">
                  <span>Total TTC</span>
                  <span className="text-green-600">
                    {(parseFloat(totals.totalTTC) - (usePoints ? loyaltyDiscount : 0)).toFixed(2)} €
                  </span>
                </div>
                
                {/* Points à gagner */}
                {invoiceType === 'invoice' && selectedCategory !== 'dematerialise' && parseFloat(totals.totalTTC) > 0 && (
                  <div className="text-sm text-green-600 flex items-center gap-1">
                    <Coins className="w-4 h-4" />
                    +{Math.floor(parseFloat(totals.totalTTC) - (usePoints ? loyaltyDiscount : 0))} points fidélité
                  </div>
                )}
              </div>

              {/* Points du client */}
              {selectedClient && clientPoints && invoiceType === 'invoice' && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-amber-800 flex items-center gap-1">
                      <Coins className="w-4 h-4" />
                      Solde fidélité
                    </span>
                    <Badge className="bg-amber-100 text-amber-700 text-lg font-bold">
                      {clientPoints.points_balance || 0} pts
                    </Badge>
                  </div>
                  
                  {/* Catégorie */}
                  <div className="mb-3">
                    <Label className="text-xs text-amber-700">Catégorie</Label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="mt-1 h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            <span className={`px-2 py-0.5 rounded ${cat.color}`}>{cat.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Avertissement catégorie exclue */}
                  {selectedCategory === 'dematerialise' && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                      <AlertTriangle className="w-3 h-3" />
                      Catégorie exclue du programme fidélité
                    </div>
                  )}
                  
                  {/* Options de remise */}
                  {discountOptions.length > 0 && selectedCategory !== 'dematerialise' && (
                    <div className="space-y-2">
                      <Label className="text-xs text-amber-700">Utiliser les points</Label>
                      <div className="space-y-1">
                        <button
                          onClick={() => applyDiscountOption(null)}
                          className={`w-full text-left p-2 rounded text-sm ${!usePoints ? 'bg-white border-2 border-amber-400' : 'bg-white/50 border border-amber-200'}`}
                        >
                          Ne pas utiliser de points
                        </button>
                        {discountOptions.map((option, idx) => (
                          <button
                            key={idx}
                            onClick={() => applyDiscountOption(option)}
                            className={`w-full text-left p-2 rounded text-sm ${usePoints && pointsToUse === option.points_needed ? 'bg-white border-2 border-amber-400' : 'bg-white/50 border border-amber-200'}`}
                          >
                            <span className="font-medium text-amber-700">{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {clientPoints.points_balance > 0 && discountOptions.length === 0 && selectedCategory !== 'dematerialise' && (
                    <p className="text-xs text-amber-600">Points insuffisants pour une remise sur cette vente</p>
                  )}
                  
                  {/* Option affichage sur facture */}
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showPointsOnInvoice}
                        onChange={(e) => setShowPointsOnInvoice(e.target.checked)}
                        className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-xs text-amber-700">
                        Afficher le solde fidélité sur la facture
                      </span>
                    </label>
                    <p className="text-xs text-amber-600/70 mt-1 ml-5">
                      (Décocher pour les factures assurance/entreprise)
                    </p>
                  </div>
                </div>
              )}

              {formData.payment_method && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Mode de paiement</p>
                  <p className="font-medium text-slate-700 flex items-center gap-2">
                    {formData.payment_method === 'Carte bancaire' && <CreditCard className="w-4 h-4" />}
                    {formData.payment_method === 'Espèces' && <Banknote className="w-4 h-4" />}
                    {formData.payment_method === 'Virement' && <Building2 className="w-4 h-4" />}
                    {formData.payment_method}
                  </p>
                </div>
              )}

              <div className="mt-6 space-y-2">
                <Button 
                  onClick={() => handleSubmit(true)} 
                  variant="outline"
                  className="w-full" 
                  disabled={savingDraft || loading}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer en brouillon
                </Button>
                <Button 
                  onClick={() => handleSubmit(false)} 
                  className="w-full bg-green-600 hover:bg-green-700" 
                  disabled={loading || savingDraft}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Créer la {invoiceType === 'invoice' ? 'facture' : 'devis'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* New Client Modal */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">
                {editingClient ? 'Modifier la fiche client' : 'Nouveau client'}
              </h2>
              <button onClick={handleCloseClientModal} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Type de client */}
              <div>
                <Label className="text-sm font-medium">Type de client</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="professionnel"
                      checked={newClient.client_type === 'professionnel'}
                      onChange={(e) => setNewClient({ ...newClient, client_type: e.target.value })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <Building2 className="w-4 h-4 text-blue-600" />
                    <span>Professionnel</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="particulier"
                      checked={newClient.client_type === 'particulier'}
                      onChange={(e) => setNewClient({ ...newClient, client_type: e.target.value })}
                      className="w-4 h-4 text-green-600"
                    />
                    <UserCircle className="w-4 h-4 text-green-600" />
                    <span>Particulier</span>
                  </label>
                </div>
              </div>

              {/* Informations de l'entreprise */}
              {newClient.client_type === 'professionnel' && (
                <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                  <h3 className="font-medium text-blue-900 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Informations de l'entreprise
                  </h3>
                  <div>
                    <Label>Nom de l'entreprise *</Label>
                    <Input
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      placeholder="Nom de l'entreprise"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>N° de SIREN ou SIRET</Label>
                      <Input
                        value={newClient.siret}
                        onChange={(e) => setNewClient({ ...newClient, siret: e.target.value })}
                        placeholder="XXX XXX XXX XXXXX"
                      />
                    </div>
                    <div>
                      <Label>N° de TVA intracom.</Label>
                      <Input
                        value={newClient.tva_number}
                        onChange={(e) => setNewClient({ ...newClient, tva_number: e.target.value })}
                        placeholder="FRXXXXXXXXXX"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Nom pour particulier */}
              {newClient.client_type === 'particulier' && (
                <div>
                  <Label>Nom complet *</Label>
                  <Input
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    placeholder="Prénom Nom"
                  />
                </div>
              )}

              {/* Contact */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={newClient.phone}
                    onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                    placeholder="06 XX XX XX XX"
                  />
                </div>
              </div>

              {/* Adresse avec autocomplétion */}
              <div className="space-y-3">
                <h3 className="font-medium text-slate-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Adresse
                </h3>
                
                <div className="relative" ref={addressRef}>
                  <Label>Rechercher une adresse</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={addressSearch}
                      onChange={(e) => {
                        setAddressSearch(e.target.value);
                        searchAddress(e.target.value);
                      }}
                      onFocus={() => addressResults.length > 0 && setShowAddressResults(true)}
                      placeholder="Tapez une adresse..."
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Address Results Dropdown */}
                  {showAddressResults && addressResults.length > 0 && (
                    <div className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                      {addressResults.map((feature, index) => (
                        <button
                          key={index}
                          onClick={() => handleSelectAddress(feature)}
                          className="w-full px-4 py-2 text-left hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <span>{feature.properties.label}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <div>
                  <Label>Adresse</Label>
                  <Input
                    value={newClient.address}
                    onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                    placeholder="Numéro et rue"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Code postal</Label>
                    <Input
                      value={newClient.postal_code}
                      onChange={(e) => setNewClient({ ...newClient, postal_code: e.target.value })}
                      placeholder="69000"
                    />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input
                      value={newClient.city}
                      onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                      placeholder="Lyon"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <Button onClick={handleCloseClientModal} variant="outline" className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleCreateClient} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="save-client-btn">
                {editingClient ? 'Enregistrer les modifications' : 'Créer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
